import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDependency } from "../../../../contexts/DependencyProvider";
import {
  ListStatusRequest,
  ListStatusType,
} from "../../../../services/soap/project/requests/ListStatusRequest";
import {
  Flex,
  View,
  TextField,
  MenuTrigger,
  Menu,
  Item,
  ActionButton,
  Text,
} from "@adobe/react-spectrum";
import Edit from "@spectrum-icons/workflow/Edit";
import { enableRipple } from "@syncfusion/ej2-base";
import {
  ContextMenuComponent,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { AtollonIcon, MoreIcon, ProjectIconBlue } from "../../Icons/IconsLib";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { GetContextPathRequest } from "../../../../services/soap/project/requests/GetContextPathRequest";
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../state/store";
import { setSelectedItem } from "../../../../state/Finder/finderSlice";
import { GetProjectRequest } from "../../../../services/soap/project/requests/GetProjectRequest";
import { UpdateProjectRequest } from "../../../../services/soap/project/requests/UpdateProjectRequest";
import { CheckWorkFlowQueueRequest } from "../../../../services/soap/features/requests/CheckWorkFlowQueueRequest";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { invokeFinderApp } from "../../../../utils/NavigationUtils";
import styles from "./HeaderToolbarComponent.module.css";
import { getDisplayContextPathNodes } from "./contextPathUtils";

interface IProjectComponentMobileProps {
  headerData: any;
  statusColor: (color: string) => string;
  menuItems: MenuItemModel[];
  handleMenuItemClick?: (args: any) => void;
  onAI?: (item: FPAData) => void;
  selectedItem?: FPAData;
  onCopy?: (item: FPAData) => void;
  onMove?: (item: FPAData) => void;
}
enableRipple(true);
const ProjectComponentMobile = ({
  headerData,
  statusColor,
  menuItems,
  handleMenuItemClick,
  onAI,
  selectedItem,
  onCopy,
  onMove,
}: IProjectComponentMobileProps) => {
  const { store, projectService, featureService } = useDependency();
  const [projectStatusList, setProjectStatusList] = useState<any>([]);
  const [fullPath, setFullPath] = useState<string>("");
  const [projectTypeName, setProjectTypeName] = useState<string>("");
  const [projectData, setProjectData] = useState<any>([]);
  let [activityStatus, setActivityStatus] = useState("");
  const { t } = useTranslation();
  const contextMenuRef = useRef<ContextMenuComponent | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingRefId, setIsEditingRefId] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedRefId, setEditedRefId] = useState("");

  // Local state to track updated values
  const [displayName, setDisplayName] = useState<string>("");
  const [displayRefId, setDisplayRefId] = useState<string>("");

  // State to control menu open/close for double-click support
  const [isNameMenuOpen, setIsNameMenuOpen] = useState(false);
  const [isRefIdMenuOpen, setIsRefIdMenuOpen] = useState(false);

  // Update display values when headerData changes
  useEffect(() => {
    setDisplayName(headerData?.name || "");
    setDisplayRefId(headerData?.refId || "");
  }, [headerData?.name, headerData?.refId]);

  useEffect(() => {
    (async () => {
      headerData && (await getFullPath());
      await getProjectData();
      await getProjectStatusArray();
      setActivityStatus(headerData?.customState);
    })();
  }, [headerData?.id]);

  React.useEffect(() => {
    // Add the click event listener to the target element
    const targetElement = document.querySelector(".context-target");
    if (targetElement) {
      targetElement.addEventListener("click", handleContextMenu);
    }

    // Cleanup
    return () => {
      if (targetElement) {
        targetElement.removeEventListener("click", handleContextMenu);
      }
    };
  }, []);

  const getFullPath = async () => {
    const response = await projectService.getContextPath(
      new GetContextPathRequest(
        store.Server,
        store.SessionId,
        headerData?.id || 0,
      ),
    );
    setProjectTypeName(response.LIST[0].CONTEXT?.typeName);
    const modifiedList = getDisplayContextPathNodes(
      response.LIST,
      headerData?.id,
      false,
    );
    setFullPath(modifiedList.map((item) => item.name).join(" > "));
  };

  const getProjectStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listProjectStatus2(
      new ListStatusRequest(
        store.Server,
        store.SessionId,
        ListStatusType.Project,
      ),
    );

    if (res.result === "OK" && res.STATUS.length > 0) {
      res?.STATUS.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color, TYPE } = status;
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color, types: TYPE });
      });

      let filtered_status = statusArray.filter((status: any) =>
        status.types.find((type: any) => type.id === headerData?.projectType),
      );

      setProjectStatusList(filtered_status);
    }
  }, []);

  function itemTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5}>{data.name}</View>
      </Flex>
    );
  }
  function valueTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View
          UNSAFE_className={styles.dot}
          UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}
        ></View>
        <View marginTop={5} UNSAFE_style={{ marginLeft: "10px" }}>
          <span title={data?.name}>
            {data?.name && data?.name.length > 23
              ? data?.name.substring(0, 23) + "..."
              : data?.name}
          </span>
        </View>
      </Flex>
    );
  }

  const handleContextMenu = (event: any) => {
    // Prevent the default context menu
    event.preventDefault();

    // Open the custom context menu at the click position
    if (contextMenuRef.current) {
      contextMenuRef.current.open(event.pageY, event.pageX);
    }
  };
  const handleAIClicked = () => {
    if (onAI) {
      onAI && selectedItem && onAI(selectedItem);
    }
  };

  async function getProjectData() {
    if (selectedItem) {
      let { PROJECT } = await projectService.getProject(
        new GetProjectRequest(store.Server, store.SessionId, selectedItem.id),
      );
      setProjectData(PROJECT);
    }
  }
  async function updateProjectStatus(e: any) {
    let resProject = await projectService.updateProject(
      new UpdateProjectRequest(store.Server, store.SessionId, {
        ...projectData,
        customState: e.value,
      }),
    );
    if (resProject.result == "OK") {
      await featureService.checkWorkFlowQueue(
        new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
      );
      ToastQueue.positive(
        t("status_updated_successfully", { ns: "layout_components" }),
        { timeout: 1000 },
      );
    } else {
      ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  // Handler to start editing project name
  const handleEditName = () => {
    setEditedName(displayName || "");
    setIsEditingName(true);
    setIsNameMenuOpen(false);
  };

  // Handler to start editing refId
  const handleEditRefId = () => {
    setEditedRefId(displayRefId || "");
    setIsEditingRefId(true);
    setIsRefIdMenuOpen(false);
  };

  // Handler to save project name
  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === displayName) {
      setIsEditingName(false);
      return;
    }
    try {
      let resProject = await projectService.updateProject(
        new UpdateProjectRequest(store.Server, store.SessionId, {
          ...projectData,
          name: editedName,
        }),
      );
      if (resProject.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("project_name_updated_successfully", {
            ns: "layout_components",
            defaultValue: "Project name updated successfully",
          }),
          { timeout: 1000 },
        );
        setDisplayName(editedName);
        dispatch(
          setSelectedItem({
            item: selectedItem ? { ...selectedItem, title: editedName } : null,
            show_context: false,
          }),
        );
        await getProjectData();
      } else {
        ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_project", {
            ns: "layout_components",
            defaultValue: "Failed to update project",
          }),
        { timeout: 1000 },
      );
    }
    setIsEditingName(false);
  };

  // Handler to save refId
  const handleSaveRefId = async () => {
    if (editedRefId === displayRefId) {
      setIsEditingRefId(false);
      return;
    }
    try {
      let resProject = await projectService.updateProject(
        new UpdateProjectRequest(store.Server, store.SessionId, {
          ...projectData,
          refId: editedRefId,
        }),
      );
      if (resProject.result === "OK") {
        await featureService.checkWorkFlowQueue(
          new CheckWorkFlowQueueRequest(store.Server, store.SessionId),
        );
        ToastQueue.positive(
          t("refid_updated_successfully", {
            ns: "layout_components",
            defaultValue: "RefId updated successfully",
          }),
          { timeout: 1000 },
        );
        setDisplayRefId(editedRefId);
        await getProjectData();
      } else {
        ToastQueue.negative(resProject.EXCEPTION?.message, { timeout: 1000 });
      }
    } catch (error: any) {
      ToastQueue.negative(
        error?.message ||
          t("failed_to_update_project", {
            ns: "layout_components",
            defaultValue: "Failed to update project",
          }),
        { timeout: 1000 },
      );
    }
    setIsEditingRefId(false);
  };

  // Handler to cancel editing
  const handleCancelEdit = (type: "name" | "refId") => {
    if (type === "name") {
      setIsEditingName(false);
      setEditedName("");
    } else {
      setIsEditingRefId(false);
      setEditedRefId("");
    }
  };

  const handleMasterProjectInfoClick = (id: string) => {
    if (id) {
      invokeFinderApp(id, FPADataTypes.PROJECT);
    }
  };

  return (
    <Flex UNSAFE_className={styles.header}>
      <View UNSAFE_className={styles.header_main}>
        <Flex direction="row" width={"100%"} justifyContent={"space-between"}>
          <View>
            {projectStatusList.length > 0 && (
              <DropDownListComponent
                width={"250px"}
                dataSource={projectStatusList}
                fields={{ text: "name", value: "id" }}
                showClearButton={true}
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                value={activityStatus}
                onChange={(e: any) => {
                  updateProjectStatus(e);
                }}
              />
            )}
          </View>
          <Flex direction={"row"}>
            <View>
              <div onClick={() => handleAIClicked()}>
                <AtollonIcon size={32} />
              </div>
            </View>
            <View>
              <ContextMenuComponent
                target=".context-target"
                id="contextmenu"
                ref={contextMenuRef}
                items={menuItems}
                select={handleMenuItemClick}
              />
              <span className="context-target">
                <MoreIcon size={32} />
              </span>
            </View>
          </Flex>
        </Flex>
        <View UNSAFE_className={styles.header_child} marginTop="15px">
          <View UNSAFE_className={styles.frame_parent}>
            <Flex
              direction={"row"}
              justifyContent={"space-between"}
              width={"100%"}
            >
              <View
                UNSAFE_style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                }}
              >
                <View>
                  <ProjectIconBlue size={32} />
                </View>
                <View>
                  {isEditingName ? (
                    <TextField
                      value={editedName}
                      onChange={setEditedName}
                      onBlur={handleSaveName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveName();
                        if (e.key === "Escape") handleCancelEdit("name");
                      }}
                      width="220px"
                      autoFocus
                      isQuiet
                    />
                  ) : (
                    <MenuTrigger
                      trigger="longPress"
                      isOpen={isNameMenuOpen}
                      onOpenChange={setIsNameMenuOpen}
                    >
                      <div onDoubleClick={() => setIsNameMenuOpen(true)}>
                        <ActionButton
                          isQuiet
                          UNSAFE_style={{
                            padding: 0,
                            minWidth: "auto",
                            background: "transparent",
                            border: "none",
                            cursor: "text",
                            height: "auto",
                            fontWeight: "bold",
                          }}
                          UNSAFE_className={styles.activity_name}
                        >
                          <span
                            style={{ fontSize: "16px", padding: "2px" }}
                            title={displayName}
                          >
                            {displayName?.length > 30
                              ? displayName.substring(0, 30) + "..."
                              : displayName}
                          </span>
                        </ActionButton>
                      </div>
                      <Menu
                        onAction={(key) => key === "edit" && handleEditName()}
                      >
                        <Item key="edit">
                          <Edit size="S" />
                          <Text>
                            {t("edit_name", {
                              ns: "layout_components",
                              defaultValue: "Edit Name",
                            })}
                          </Text>
                        </Item>
                      </Menu>
                    </MenuTrigger>
                  )}
                </View>
              </View>
            </Flex>
            <View UNSAFE_className={styles.folder_name_parent}>
              <View UNSAFE_className={styles.folder_name}>{fullPath}</View>
            </View>
            <View UNSAFE_className={styles.tag_parent} marginTop={"5px"}>
              <Flex
                direction={"row"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <View UNSAFE_className={styles.tag}>
                  <View UNSAFE_className={styles.activity_type}>
                    <View UNSAFE_className={styles.activity_type_text}>
                      {projectTypeName}
                    </View>
                    {isEditingRefId ? (
                      <>
                        <View UNSAFE_className={styles.divider}></View>
                        <TextField
                          value={editedRefId}
                          onChange={setEditedRefId}
                          onBlur={handleSaveRefId}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveRefId();
                            if (e.key === "Escape") handleCancelEdit("refId");
                          }}
                          width="130px"
                          autoFocus
                          isQuiet
                        />
                      </>
                    ) : displayRefId ? (
                      <>
                        <View
                          UNSAFE_className={styles.divider}
                          UNSAFE_style={{ backgroundColor: "#fff" }}
                        ></View>
                        <MenuTrigger
                          trigger="longPress"
                          isOpen={isRefIdMenuOpen}
                          onOpenChange={setIsRefIdMenuOpen}
                        >
                          <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                            <ActionButton
                              isQuiet
                              UNSAFE_style={{
                                padding: 0,
                                minWidth: "auto",
                                background: "transparent",
                                border: "none",
                                cursor: "text",
                                height: "auto",
                              }}
                            >
                              <span
                                style={{ fontSize: "12px", padding: "5px" }}
                                className={styles.ref_id}
                              >
                                {displayRefId}
                              </span>
                            </ActionButton>
                          </div>
                          <Menu
                            onAction={(key) =>
                              key === "edit" && handleEditRefId()
                            }
                          >
                            <Item key="edit">
                              <Edit size="S" />
                              <Text>
                                {t("edit_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Edit RefID",
                                })}
                              </Text>
                            </Item>
                          </Menu>
                        </MenuTrigger>
                      </>
                    ) : (
                      <>
                        <View
                          UNSAFE_className={styles.divider}
                          UNSAFE_style={{ backgroundColor: "#fff" }}
                        ></View>
                        <MenuTrigger
                          trigger="longPress"
                          isOpen={isRefIdMenuOpen}
                          onOpenChange={setIsRefIdMenuOpen}
                        >
                          <div onDoubleClick={() => setIsRefIdMenuOpen(true)}>
                            <ActionButton
                              isQuiet
                              UNSAFE_style={{
                                padding: 0,
                                minWidth: "auto",
                                background: "transparent",
                                border: "none",
                                cursor: "text",
                                height: "auto",
                              }}
                            >
                              <span
                                style={{ fontSize: "12px", padding: "5px" }}
                                className={styles.ref_id}
                              >
                                {t("add_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Add RefID",
                                })}
                              </span>
                            </ActionButton>
                          </div>
                          <Menu
                            onAction={(key) =>
                              key === "edit" && handleEditRefId()
                            }
                          >
                            <Item key="edit">
                              <Edit size="S" />
                              <Text>
                                {t("add_refid", {
                                  ns: "layout_components",
                                  defaultValue: "Add RefID",
                                })}
                              </Text>
                            </Item>
                          </Menu>
                        </MenuTrigger>
                      </>
                    )}
                  </View>
                </View>
              </Flex>
            </View>
            <View UNSAFE_className={styles.master_project_info}>
              <span
                onClick={() =>
                  handleMasterProjectInfoClick(headerData?.masterFolderId)
                }
              >
                {headerData?.masterFolderName}
              </span>{" "}
              &gt;{" "}
              <span
                onClick={() =>
                  handleMasterProjectInfoClick(headerData?.masterProjectId)
                }
              >
                {headerData?.masterProjectName}
              </span>
            </View>
          </View>
        </View>
      </View>
    </Flex>
  );
};

export default ProjectComponentMobile;
